<template>
    <div class="d-flex flex-row list-view position-relative">
        <div v-if="vehicle.warrantyEligible" class="warranty-ribbon">
            <warranty-ribbon />
        </div>

        <b-link
            class="listing-image-container align-items-center d-flex flex-column"
            @click="hotSpotHandler"
        >
            <fixed-vehicle-image
                :image-url="vehicle.imageUrl"
                :backup-image-url="vehicle.genericImageUrl"
                :body-style="vehicle.bodyStyle"
                class="listing-image"
            />
        </b-link>

        <div
            class="content-container d-flex flex-column justify-content-between w-100 py-3 pr-4"
        >
            <div class="content d-flex flex-column">
                <div class="d-flex justify-content-between">
                    <div class="left-section d-flex flex-column">
                        <div class="year-make">
                            {{ yearsDisplay }} {{ vehicle.make }}
                        </div>
                        <div class="model">
                            {{ vehicle.model }}
                        </div>
                        <div class="msrp-starting font-italic">
                            MSRP starting at
                            {{ vehicle.lowestMsrp | numeral("$0,0") }}
                        </div>
                    </div>

                    <div
                        class="right-section d-flex text-right justify-content-between"
                    >
                        <div
                            class="save-up-to d-flex flex-column pr-3"
                            :class="{ invisible: !hasBiggestSavings }"
                        >
                            <div class="text font-italic">
                                save up to
                                <tooltip-disclaimer
                                    v-if="vehicle.biggestSavings"
                                    :title="vehicle.biggestSavings.disclaimer"
                                />
                            </div>
                            <div class="value">
                                {{
                                    vehicle.biggestSavings.value
                                        | numeral("$0,0")
                                }}
                            </div>
                        </div>

                        <div v-if="hasLowestPayment" class="flex-column">
                            <div class="as-low-as">
                                <span class="font-italic">
                                    as low as&nbsp;
                                </span>
                                <span class="value">
                                    {{
                                        vehicle.lowestPrice.value
                                            | numeral("$0,0")
                                    }}
                                </span>
                                <tooltip-disclaimer
                                    v-if="vehicle.lowestPrice"
                                    :title="vehicle.lowestPrice.disclaimer"
                                />
                            </div>
                            <div class="monthly-price-container">
                                <div class="monthly-price-value">
                                    {{
                                        vehicle.lowestPayment.value
                                            | numeral("$0,0")
                                    }}
                                </div>
                                <div
                                    class="monthly-price-footer font-italic text-lowercase"
                                >
                                    {{ vehicle.lowestPayment.type }} /mo.
                                    <tooltip-disclaimer
                                        v-if="vehicle.lowestPayment"
                                        :title="
                                            vehicle.lowestPayment.disclaimer
                                        "
                                    />
                                </div>
                            </div>
                        </div>
                        <div v-else-if="!hasLowestPayment" class="flex-column">
                            <div class="starting-price">
                                <span class="font-italic">
                                    starting at
                                </span>
                                <tooltip-disclaimer
                                    v-if="vehicle.lowestPrice"
                                    :title="vehicle.lowestPrice.disclaimer"
                                />
                            </div>
                            <div class="starting-price-lg">
                                {{
                                    vehicle.lowestPrice.value | numeral("$0,0")
                                }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="footer-container d-flex justify-content-between">
                <div class="certified-flag">
                    <span v-if="vehicle.certified" class="orange-bold"
                        >CarSaver Certified Dealer</span
                    >
                    <span v-if="vehicle.supplierPricingAvailable"
                        >Supplier Pricing Available</span
                    >
                </div>
                <div class="cta-container d-flex">
                    <div v-if="isListingsAvailable" class="cta split">
                        <a :href="listingsUrl" class="view-listing">
                            View Listings
                            <b-badge v-if="vehicle.count" pill variant="danger">
                                {{ vehicle.count | numeral("0,0") }}
                            </b-badge>
                        </a>
                    </div>
                    <div class="cta">
                        <a :href="buildAndPriceUrl" class="cta build-price">
                            Build & Price
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import FixedVehicleImage from "Modules/Vehicle/FixedVehicleImage";
import WarrantyRibbon from "./WarrantyRibbon";
import mixin from "../mixin";
import TooltipDisclaimer from "Components/TooltipDisclaimer/index";

export default {
    name: "ListingCard",
    components: {
        TooltipDisclaimer,
        FixedVehicleImage,
        WarrantyRibbon
    },
    mixins: [mixin],
    props: {
        vehicle: {
            type: Object,
            required: true
        }
    }
};
</script>
<style lang="scss" scoped>
.msrp-starting {
    font-size: px2rem(12);
}

.view-listing {
    position: relative;

    .badge {
        position: absolute;
        bottom: 17px;
        right: -13px;
    }
}

.certified-flag {
    font-size: px2rem(12);
    font-family: $helvetica-46-light-italic;

    > span {
        margin: 0 4px;
        white-space: nowrap;

        &.orange-bold {
            color: $orange;
            font-weight: bold;
        }
    }
}

.left-section,
.right-section {
    overflow: hidden;
}

.left-section {
    flex: 1;
}

.right-section {
    padding-right: 1px;
    min-width: 40%;
}

.cta-container {
    .cta.split:after {
        content: "|";
        margin: 0 10px;
        color: #4a4a4a;
    }
}

.as-low-as {
    font-size: px2rem(12);

    .value {
        font-family: $helvetica-55-Roman;
        font-style: normal;
    }
}

.save-up-to {
    font-size: px2rem(12);

    .value {
        font-size: px2rem(24);
        line-height: px2rem(44);
    }
}

.content-container {
    max-width: calc(100% - 240px);
}

.content {
    font-size: px2rem(11);
    font-weight: normal;
    line-height: px2rem(13);

    .model {
        font-size: px2rem(28);
        font-weight: 300;
        line-height: 34px;
    }

    .monthly-price-value,
    .starting-price-lg {
        font-size: px2rem(28);
        font-weight: 300;
        line-height: px2rem(44);
    }

    .model {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        padding-right: 10px;
    }

    .monthly-price-value {
        color: $primary-orange;
    }
}

.listing-image-container {
    min-width: 240px;

    &:hover {
        color: transparent;
    }

    .listing-image {
        width: 100%;
        max-width: 270px;
        margin-top: 15px;

        .vehicle-image-main {
            background-size: contain !important;
            background-repeat: no-repeat !important;
            background-position: center center !important;
        }
    }
}

.warranty-ribbon {
    .ribbon {
        left: -10px;
    }
}

.footer-container {
}
.list-view {
    color: $dark-gray;
    height: 145px;
    background-color: $card-background;

    &:hover,
    &:active {
        box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.35);
    }
}
</style>
<style lang="scss">
.list-view {
    .listing-image-container {
        min-width: 240px;

        .listing-image {
            width: 100%;
            max-width: 270px;
            margin-top: 15px;

            .vehicle-image-main {
                background-size: contain !important;
                background-repeat: no-repeat !important;
                background-position: center center !important;
            }
        }
    }
}
</style>
